import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonialsData } from '../data/mockData';

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-[#0a0a0a] to-[#0f0f0f] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('testimonials.title')} <span className="text-amber-500">{t('testimonials.titleHighlight')}</span> {t('testimonials.titleEnd')}
          </h2>
          <p className="text-gray-400 text-lg">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Main testimonial */}
          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-12">
            {/* Quote icon */}
            <Quote className="w-12 h-12 text-amber-500/30 mb-6" />

            {/* Rating */}
            <div className="flex gap-1 mb-6">
              {[...Array(testimonialsData[currentIndex].rating)].map((_, i) => (
                <Star key={`star-${i}`} className="w-6 h-6 fill-amber-500 text-amber-500" />
              ))}
            </div>

            {/* Testimonial text */}
            <p className="text-xl md:text-2xl text-white leading-relaxed mb-8">
              "{testimonialsData[currentIndex].text}"
            </p>

            {/* Author */}
            <div className="flex items-center gap-4">
              <img
                src={testimonialsData[currentIndex].image}
                alt={testimonialsData[currentIndex].name}
                className="w-16 h-16 rounded-full object-cover border-2 border-amber-500"
              />
              <div>
                <p className="text-white font-semibold text-lg">
                  {testimonialsData[currentIndex].name}
                </p>
                <p className="text-gray-400">
                  {testimonialsData[currentIndex].location}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={goToPrev}
              className="w-12 h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-white hover:bg-amber-500 hover:text-black transition-all duration-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonialsData.map((_, index) => (
                <button
                  key={`dot-${index}`}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentIndex(index);
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-amber-500 w-8'
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={goToNext}
              className="w-12 h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-white hover:bg-amber-500 hover:text-black transition-all duration-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mini testimonial cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {testimonialsData.slice(0, 3).map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`bg-white/5 border border-white/10 rounded-2xl p-6 transition-all duration-300 cursor-pointer hover:border-amber-500/50 ${
                index === currentIndex ? 'border-amber-500/50' : ''
              }`}
              onClick={() => {
                setIsAutoPlaying(false);
                setCurrentIndex(index);
              }}
            >
              <div className="flex gap-0.5 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={`mini-star-${i}`} className="w-4 h-4 fill-amber-500 text-amber-500" />
                ))}
              </div>
              <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                "{testimonial.text}"
              </p>
              <div className="flex items-center gap-3">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-white font-medium text-sm">{testimonial.name}</p>
                  <p className="text-gray-500 text-xs">{testimonial.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
